import { lastValueFrom } from 'rxjs';
import { SubscriptionStore2 } from '../observable/subscription.store2';
import { computed, inject, Injectable, InjectionToken } from '@angular/core';
import { SOFTLINE_SERVICE_UUID } from '../../../core.shared';
import { CancelledError } from '../../../types/errors';
import { NestedStore2Feature } from '../../nested-store2-feature';
import { LoadingState, SOFTLINE_DICTIONARY_REPOSITORY } from '../../store2.shared';
import { LoadListParameters } from '../../../repositories/abstraction';
import { DefaultDictionaryResponseCommit } from './strategies/dictionary-response-commit';
import { DictionaryStore2 } from './dictionary.store2';
import { Dictionary } from '../../../types/dictionary';

export interface ReadonlyRepositoryDictionaryState {
  loadingState: LoadingState;
  loadingError: Error | null;
}

export interface DictionaryLoadActionParameters extends LoadListParameters {
  token?: string;
}

export const SOFTLINE_FACTORY_DICTIONARY_RESPONSE_COMMIT = new InjectionToken<() => DefaultDictionaryResponseCommit<any>>('SOFTLINE_FACTORY_DICTIONARY_RESPONSE_COMMIT',
  { providedIn: 'root', factory: () => () => new DefaultDictionaryResponseCommit()});

@Injectable()
export class ReadonlyRepositoryDictionaryStore2<T extends object, TState extends ReadonlyRepositoryDictionaryState = ReadonlyRepositoryDictionaryState> extends NestedStore2Feature<TState>{

  private readonly factory = inject(SOFTLINE_FACTORY_DICTIONARY_RESPONSE_COMMIT)
  protected readonly commitResponse =  this.factory();

  protected readonly dictionary = inject(DictionaryStore2<T>);
  protected readonly store = inject(DictionaryStore2<T>);
  protected readonly subscription = inject(SubscriptionStore2);
  protected readonly service = inject(SOFTLINE_DICTIONARY_REPOSITORY);
  protected readonly uuid = inject(SOFTLINE_SERVICE_UUID);

  loadingState = computed(() => this.state().loadingState);
  loadingError = computed(() => this.state().loadingError);

  constructor() {
    super();
  }

  async load(params: DictionaryLoadActionParameters): Promise<Dictionary<T>> {
    this.commitPatch({ loadingState: 'loading', loadingError: null } as Partial<TState>);
    const token = params.token ?? this.uuid();
    try {
      const value = await lastValueFrom(this.subscription.observe(
        token,
        this.service.load(params)
      ));
      this.commitPatch({ loadingState: 'loaded' } as Partial<TState>);
      this.commitResponse.set(value);
      return value;
    } catch (e) {
      if (e instanceof CancelledError)
        this.commitPatch({ loadingState: 'canceled' } as Partial<TState>);
      else
        this.commitPatch({ loadingState: 'failed', loadingError: e } as Partial<TState>);
      throw e;
    }
  }

  async loadItem(key: string, params: DictionaryLoadActionParameters): Promise<T> {
    this.commitPatch({ loadingState: 'loading', loadingError: null } as Partial<TState>);
    const token = params.token ?? this.uuid();
    try {
      const value = await lastValueFrom(this.subscription.observe(
        token,
        this.service.loadItem(key, params)
      ));
      this.commitPatch({ loadingState: 'loaded' } as Partial<TState>);
      this.commitResponse.set(value);
      return value;
    } catch (e) {
      if (e instanceof CancelledError)
        this.commitPatch({ loadingState: 'canceled' } as Partial<TState>);
      else
        this.commitPatch({ loadingState: 'failed', loadingError: e } as Partial<TState>);
      throw e;
    }
  }

  async loadOnce(params: DictionaryLoadActionParameters): Promise<Dictionary<T>> {
    const existing = this.dictionary.state();
    if(existing)
      return existing;
    return await this.load(params);
  }

  async loadItemOnce(key: string, params: DictionaryLoadActionParameters): Promise<T> {
    const existing = this.dictionary.state()[key];
    if(existing)
      return existing;
    return await this.loadItem(key, params);
  }

  async cancel(token: string): Promise<void> {
    this.subscription.cancel(token);
  }

  override getDefaultState(): TState {
    return {
      loadingState: null,
      loadingError: null,
    } as TState;
  }
}
